/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  ArrowPathIcon,
  BoltIcon,
  PowerIcon,
} from "@heroicons/react/24/outline";
import { MonitorAgent } from "@/api/monitor";
import {
  getMonitorAgentPower,
  postMonitorAgentPower,
  postMonitorAgentWake,
  PowerAction,
} from "@/api/serverDetail";
import { showToast } from "@/components/common/Toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export type PowerTransition = "reboot" | "shutdown" | "wake";

interface PowerControlsProps {
  agent: MonitorAgent;
  online: boolean;
  onTransition: (kind: PowerTransition) => void;
}

type Pending = PowerAction | "wake" | null;

export const PowerControls: React.FC<PowerControlsProps> = ({ agent, online, onTransition }) => {
  const [pending, setPending] = useState<Pending>(null);
  const [typed, setTyped] = useState("");

  const info = useQuery({
    queryKey: ["server-power", agent.id],
    queryFn: () => getMonitorAgentPower(agent.id),
    refetchInterval: 15000,
    enabled: agent.enabled,
    retry: false,
  });

  const hostname = info.data?.agent?.hostname ?? agent.name;
  const canAct = !!info.data?.agent?.enabled && online;
  const canWake = !!info.data?.wake.available && !online;

  const close = () => {
    setPending(null);
    setTyped("");
  };

  const powerMutation = useMutation({
    mutationFn: (action: PowerAction) => postMonitorAgentPower(agent.id, action, typed.trim()),
    onSuccess: (_, action) => {
      showToast(action === "reboot" ? "Reboot requested" : "Shutdown requested", "success", {
        description: `${agent.name} will go down in a few seconds`,
      });
      onTransition(action);
      close();
    },
    onError: (e: Error) => showToast("Power action failed", "error", { description: e.message }),
  });

  const wakeMutation = useMutation({
    mutationFn: () => postMonitorAgentWake(agent.id),
    onSuccess: () => {
      showToast("Wake-on-LAN sent", "success", {
        description: `Magic packet sent via ${info.data?.wake.router ?? "router"}; the server needs 1-3 minutes to boot`,
      });
      onTransition("wake");
      close();
    },
    onError: (e: Error) => showToast("Wake-on-LAN failed", "error", { description: e.message }),
  });

  if (!canAct && !canWake) return null;

  const busy = powerMutation.isPending || wakeMutation.isPending;
  const isPowerAction = pending === "reboot" || pending === "shutdown";
  const confirmOk = pending === "wake" || typed.trim().toLowerCase() === hostname.toLowerCase();

  const submit = () => {
    if (!pending || !confirmOk) return;
    if (pending === "wake") wakeMutation.mutate();
    else powerMutation.mutate(pending);
  };

  return (
    <>
      {canAct && (
        <>
          <button className="gh-btn gh-btn--danger" onClick={() => setPending("reboot")}>
            <ArrowPathIcon /> Reboot
          </button>
          <button className="gh-btn gh-btn--danger" onClick={() => setPending("shutdown")}>
            <PowerIcon /> Shut down
          </button>
        </>
      )}
      {canWake && (
        <button className="gh-btn gh-btn--go" onClick={() => setPending("wake")}>
          <BoltIcon /> Power on
        </button>
      )}

      <Dialog open={pending !== null} onOpenChange={(open) => !open && !busy && close()}>
        <DialogContent className="gh sv max-w-md !bg-[var(--gh-panel)] !border-[var(--gh-border)]">
          <DialogHeader>
            <DialogTitle className="text-[var(--gh-text)]">
              {pending === "reboot" && "Reboot server"}
              {pending === "shutdown" && "Shut down server"}
              {pending === "wake" && "Power on server"}
            </DialogTitle>
            <DialogDescription className="text-[var(--gh-dim)]">
              {pending === "reboot" &&
                "The host and everything running on it (VMs, containers, storage) restarts. Services are unavailable for a few minutes."}
              {pending === "shutdown" &&
                "The host powers off completely. It can only be started again with Wake-on-LAN from the router or the power button."}
              {pending === "wake" &&
                `A Wake-on-LAN magic packet is sent through ${info.data?.wake.router ?? "the router"}. The server boots in 1-3 minutes.`}
            </DialogDescription>
          </DialogHeader>

          {isPowerAction && (
            <div className="grid gap-2">
              <label className="sv-note" htmlFor="sv-confirm">
                Type <span className="gh-kbd">{hostname}</span> to confirm
              </label>
              <input
                id="sv-confirm"
                autoFocus
                autoComplete="off"
                spellCheck={false}
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submit()}
                placeholder={hostname}
                className="sv-mono w-full border border-[var(--gh-border)] bg-[var(--gh-panel-2)] px-3 py-2 text-sm text-[var(--gh-text)] outline-none focus:border-[var(--gh-signal)]"
              />
            </div>
          )}

          <DialogFooter>
            <button className="gh-btn" onClick={close} disabled={busy}>
              Cancel
            </button>
            <button
              className={`gh-btn ${pending === "wake" ? "gh-btn--go" : "gh-btn--danger"}`}
              onClick={submit}
              disabled={!confirmOk || busy}
            >
              {busy
                ? "Working..."
                : pending === "reboot"
                  ? "Reboot now"
                  : pending === "shutdown"
                    ? "Shut down now"
                    : "Send magic packet"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
